import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { coursesAPI } from '../../services/api';
import { getUserAvatarSrc } from '../../utils/avatarUtils';

const AuthorsModal = ({ open, onClose, courseId }) => {
  const theme = useTheme();
  const { t } = useTranslation(['courses', 'common']);

  // Authors states
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load authors data
  const loadAuthors = useCallback(async () => {
    if (!courseId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await coursesAPI.getAuthors(courseId);
      setAuthors(response.data.authors || []);
    } catch (err) {
      console.error('Error loading authors:', err);
      setError(t('courses:courseDetail.authorsModal.loadError'));
    } finally {
      setLoading(false);
    }
  }, [courseId, t]);

  // Load data when modal opens
  useEffect(() => {
    if (open) {
      loadAuthors();
    }
  }, [open, loadAuthors]);

  // Get role display info
  const getRoleInfo = (role) => {
    switch (role) {
      case 'instructor':
        return {
          label: t('courses:courseDetail.authorsModal.roles.instructor'),
          color: 'primary',
        };
      case 'module_author':
        return {
          label: t('courses:courseDetail.authorsModal.roles.moduleAuthor'),
          color: 'secondary',
        };
      case 'lesson_author':
        return {
          label: t('courses:courseDetail.authorsModal.roles.lessonAuthor'),
          color: 'success',
        };
      default:
        return {
          label: role,
          color: 'default',
        };
    }
  };

  // Get user roles display info
  const getUserRoleInfo = (userRoles) => {
    if (!userRoles || userRoles.length === 0) return null;

    // Map user roles to display info
    const roleMap = {
      admin: {
        label: t('courses:courseDetail.authorsModal.userRoles.admin'),
        color: 'error',
      },
      instructor: {
        label: t('courses:courseDetail.authorsModal.userRoles.instructor'),
        color: 'primary',
      },
      moderator: {
        label: t('courses:courseDetail.authorsModal.userRoles.moderator'),
        color: 'warning',
      },
      student: {
        label: t('courses:courseDetail.authorsModal.userRoles.student'),
        color: 'info',
      },
    };

    return userRoles.map(
      (role) => roleMap[role] || { label: role, color: 'default' }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '600px',
          width: '100%',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          {t('courses:courseDetail.authorsModal.title')}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && authors.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t('courses:courseDetail.authorsModal.noAuthors')}
            </Typography>
          </Box>
        )}

        {!loading && !error && authors.length > 0 && (
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              pr: 1,
            }}
          >
            {authors.map((author) => {
              const authorName =
                author.name ||
                t('courses:courseDetail.authorsModal.unknownAuthor');

              return (
                <Box
                  key={author._id}
                  component={Link}
                  to={`/profile/${author._id}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1.5,
                    borderRadius: 1.5,
                    mb: 0.5,
                    color: 'text.primary',
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      src={getUserAvatarSrc(author.avatar)}
                      alt={authorName}
                      sx={{
                        width: 36,
                        height: 36,
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        fontSize: '0.813rem',
                        fontWeight: 'bold',
                      }}
                    >
                      {authorName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.3 }}
                      >
                        {authorName}
                        {author._id && (
                          <PersonIcon
                            sx={{
                              ml: 1,
                              fontSize: '0.875rem',
                              color: theme.palette.text.secondary,
                              verticalAlign: 'middle',
                            }}
                          />
                        )}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {author.email || 'No email'}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      alignItems: 'flex-end',
                    }}
                  >
                    {/* Author Roles */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        justifyContent: 'flex-end',
                      }}
                    >
                      {author.authorRoles?.map((role) => {
                        const roleInfo = getRoleInfo(role);
                        return (
                          <Chip
                            key={role}
                            label={roleInfo.label}
                            size="small"
                            sx={{
                              fontSize: '0.688rem',
                              fontWeight: 600,
                              height: 20,
                              backgroundColor:
                                theme.palette[roleInfo.color]?.main + '20',
                              color: theme.palette[roleInfo.color]?.main,
                              '& .MuiChip-label': {
                                px: 1,
                                py: 0.25,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                    {/* User Roles */}
                    {author.userRoles && author.userRoles.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          justifyContent: 'flex-end',
                        }}
                      >
                        {getUserRoleInfo(author.userRoles)?.map(
                          (roleInfo, index) => (
                            <Chip
                              key={index}
                              label={roleInfo.label}
                              size="small"
                              sx={{
                                fontSize: '0.688rem',
                                fontWeight: 600,
                                height: 20,
                                textTransform: 'capitalize',
                                backgroundColor:
                                  theme.palette[roleInfo.color]?.main + '15',
                                color: theme.palette[roleInfo.color]?.main,
                                '& .MuiChip-label': {
                                  px: 1,
                                  py: 0.25,
                                },
                              }}
                            />
                          )
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthorsModal;
